import { useMemo } from 'react';
import { SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { useQuery, UseQueryOptions } from 'react-query';
import { SMSDataSharedUtils, SMSDataV4 } from '@frontend/api-sms-data';
import { SchemaTaskCenter } from '../../service';
import { IO } from '../../types';
import { generateContext } from '../../utils/generate-context';
import { defaultOptions } from '../config';
import { queryRequestKeys } from '../keys';

export const useSuggestTaskDetails = <E = unknown, T = IO.SuggestTaskDetails['output']>(
  req: IO.SuggestTaskDetails['input'],
  options?: UseQueryOptions<IO.SuggestTaskDetails['output'], E, T>
) => {
  const queryKey = queryRequestKeys.suggestTaskDetails(req);
  return useQuery<IO.SuggestTaskDetails['output'], E, T>(
    queryKey,
    () => SchemaTaskCenter.SuggestTaskDetails(req),
    options
  );
};

export const useSuggestTaskDetailsWithContext = (
  req: IO.UseSuggestTaskDetailsWithContextInput,
  options?: UseQueryOptions<IO.SuggestTaskDetails['output'], unknown, IO.SuggestTaskDetails['output']>
) => {
  const { sourceId, createdAt, threadId, locationId, patientName, count = 5 } = req;
  const { isSmsMessageItem } = SMSDataSharedUtils.threadItemsUtils;
  // There is a query available but that's an infinite query. Instead of using that, we can make a separate query here
  // to fetch the messages around the message for which we want to suggest task details.
  const fetchGetUnifiedThread = SMSDataV4.Queries.useFetchGetUnifiedThread();

  const { data: surroundingMessages, isLoading: isMessagesLoading } = useQuery<SMS[], unknown, SMS[]>({
    queryKey: queryRequestKeys.surroundingMessages(req),
    queryFn: async () => {
      const response = await fetchGetUnifiedThread({
        request: {
          locationId,
          threadId,
          // fetch count messages up and down including the current message to provide better context for AI suggestions
          pageSize: count * 2 + 1,
          filter: {
            focusThreadItem: sourceId,
            focusCreatedAt: createdAt,
            showDeleted: true,
          },
        },
      });

      const items = response?.thread?.items ?? [];
      return items.filter(isSmsMessageItem).map((item) => item.smsMessage);
    },
    ...defaultOptions,
    enabled: !!sourceId && !!threadId && !!createdAt && (options?.enabled ?? true),
  });

  const context = useMemo(() => generateContext(sourceId, surroundingMessages), [surroundingMessages, sourceId]);

  const suggestQuery = useSuggestTaskDetails(
    { context, patientName, locationId },
    { ...options, enabled: (options?.enabled ?? true) && !!context }
  );

  return {
    ...suggestQuery,
    isLoading: isMessagesLoading || suggestQuery.isLoading,
  };
};
